import { useEffect, useState, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { initializeChatListener, startNewProject, resetChatModuleState, rehydrateConversation, setChatHistoryLoading, cancelOngoingConversationLoad } from "./ChatSlice_Store";
import { acquireProjectSwitchLock } from "./projectSwitchLock";
import { resetChatForNewProject } from "./ChatSlice_Store";
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import {
  selectCurrentProjectId,
  fetchProject,
  setProjectId,
  fetchUserProjects,
  selectUserProjects,
  selectUserProjectsLoading
} from "./ProjectSlice_Store";
import { closeAllDocumentPreviews } from "./DocumentPreviewSlice_Store";
import { clearResults } from "./ResultsSlice_Store";
import { selectLayoutMode, setLayoutMode } from "./LayoutSlice_Store";

// Import child components
import Header_Component from "./Header_Component";
import Sidebar_Component from "./Sidebar_Component";
import MainContentArea_Component from "./MainContentArea_Component";
import WelcomeModal from "./components/WelcomeModal_Component";
import { useIsTablet } from "./hooks/useIsTablet";
import { useVirtualKeyboard } from "./hooks/useVirtualKeyboard";

export default function MainLayout_Component(): JSX.Element {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if we're on the admin path
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Desktop sidebar state management
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() =>
    JSON.parse(localStorage.getItem('dealos.sidebarCollapsed') ?? 'false')
  );
  const [isWorkspaceForcingCollapse, setIsWorkspaceForcingCollapse] = useState<boolean>(false);
  const manualCollapsedStateRef = useRef<boolean>(isSidebarCollapsed);
  const userManuallyExpandedRef = useRef<boolean>(false);

  // Welcome modal state - auto-opens on first visit only (versioned key allows re-showing after major content updates)
  const WELCOME_MODAL_KEY = 'dealos.welcomeModalDismissed.v1';
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState<boolean>(
    () => !localStorage.getItem(WELCOME_MODAL_KEY)
  );


  // On tablet, use visualViewport to handle the iOS virtual keyboard:
  // 1. Set height to visualViewport.height (the actual visible area)
  // 2. Counteract iOS visual viewport shift via translateY(offsetTop)
  const isTablet = useIsTablet();
  const { isKeyboardOpen, offsetTop, viewportHeight } = useVirtualKeyboard({ enabled: isTablet });
  const tabletKeyboardStyle = isTablet && (isKeyboardOpen || offsetTop > 0)
    ? {
        height: `${viewportHeight}px`,
        transform: offsetTop > 0 ? `translateY(${offsetTop}px)` : undefined,
      }
    : undefined;

  // Get user authentication state
  const isAuthenticated = useAppSelector(state => state.userAuthAndAdminSlice?.isAuthenticated);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const layoutMode = useAppSelector(selectLayoutMode);
  const userProjects = useAppSelector(selectUserProjects);
  const userProjectsLoading = useAppSelector(selectUserProjectsLoading);

  // Note: Layout reconciliation moved to after rehydration completes
  // The rehydrateConversation thunk now properly sets layout mode based on workspace data


  // Persist manual sidebar collapse state to localStorage
  useEffect(() => {
    if (!isWorkspaceForcingCollapse) {
      localStorage.setItem('dealos.sidebarCollapsed', JSON.stringify(isSidebarCollapsed));
      manualCollapsedStateRef.current = isSidebarCollapsed;
    }
  }, [isSidebarCollapsed, isWorkspaceForcingCollapse]);

  // Handler for starting a new project
  const handleStartProject = (): void => {
    console.log('🆕 [NEW CHAT] Button clicked - starting new project flow');
    dispatch(startNewProject());
  };

  // Handler for clicking a project in the sidebar
  const handleProjectClick = async (projectId: string): Promise<void> => {
    console.log('🔄 [PROJECT CLICK] handleProjectClick called for projectId:', projectId);

    // Don't re-fetch if clicking the current project
    if (projectId === currentProjectId) {
      console.log('⏭️ [PROJECT CLICK] Already on this project - skipping');
      return;
    }

    // Acquire project switch lock — aborts any in-flight project switch
    const signal = acquireProjectSwitchLock();

    // Step 0: Cancel any ongoing conversation load immediately
    console.log('🔄 [PROJECT CLICK] Step 0: Cancelling any ongoing conversation load');
    cancelOngoingConversationLoad();

    // For temporary projects, just start a new project flow - no loading needed
    if (projectId.startsWith('temp-')) {
      console.log('🔄 [PROJECT CLICK] Temporary project - starting fresh chat');
      dispatch(setChatHistoryLoading(false));
      dispatch(startNewProject());  // startNewProject acquires its own lock
      return;
    }

    // Step 1: Show loading spinner immediately for instant feedback
    console.log('🔄 [PROJECT CLICK] Step 1: Showing loading spinner immediately');
    dispatch(setChatHistoryLoading(true));

    // Step 2: Clear workspace and reset chat for new project
    console.log('🔄 [PROJECT CLICK] Step 2: Clearing workspace and resetting chat');
    dispatch(clearResults());
    dispatch(closeAllDocumentPreviews());
    dispatch(resetChatForNewProject());
    dispatch(setLayoutMode('chat'));

    // Step 3: Reset DirectLine connection and chat module state
    console.log('🔄 [PROJECT CLICK] Step 3: Resetting chat module state');
    await resetChatModuleState();
    if (signal.aborted) {
      console.log('🔒 [PROJECT CLICK] Superseded after resetChatModuleState — aborting');
      return;
    }

    // Step 4: Set project ID immediately
    console.log('🔄 [PROJECT CLICK] Step 4: Setting project ID');
    dispatch(setProjectId(projectId));

    // Update URL
    const params = new URLSearchParams(searchParams);
    params.set('projectId', projectId);
    setSearchParams(params, { replace: true });

    // Step 5: Reinitialize chat listener
    console.log('🔄 [PROJECT CLICK] Step 5: Reinitializing chat listener');
    await dispatch(initializeChatListener(signal));
    if (signal.aborted) {
      console.log('🔒 [PROJECT CLICK] Superseded after initializeChatListener — aborting');
      return;
    }

    // Step 6: Fetch project details, then rehydrate conversation
    console.log('🔄 [PROJECT CLICK] Step 6: Fetching project and rehydrating conversation');
    dispatch(fetchProject(projectId))
      .unwrap()
      .then(() => {
        if (signal.aborted) return;
        console.log('✅ [PROJECT CLICK] Project fetched - starting rehydration');
        return dispatch(rehydrateConversation(projectId));
      })
      .then(() => {
        if (signal.aborted) return;
        console.log('✅ [PROJECT CLICK] Project click flow complete');
      })
      .catch((error) => {
        if (signal.aborted) return;
        console.error('❌ [PROJECT CLICK] Error in project click flow:', error);
        // On any error (including 404), clear loading and show fresh chat view
        dispatch(setChatHistoryLoading(false));
        dispatch(setLayoutMode('chat'));
      });
  };

  // Fetch user projects when authenticated
  // Skip on admin routes - admin pages don't need project list
  // Note: Username no longer required - derived from bearer token by APIM
  useEffect(() => {
    if (!isAdminRoute && isAuthenticated) {
      dispatch(fetchUserProjects());
    }
  }, [isAuthenticated, dispatch, isAdminRoute]);

  // Automatic workspace collapse when layout mode changes to workspace
  useEffect(() => {
    if (layoutMode === 'workspace' && !isWorkspaceForcingCollapse && !userManuallyExpandedRef.current) {
      // Cache the manual state and force collapse
      manualCollapsedStateRef.current = isSidebarCollapsed;
      setIsSidebarCollapsed(true);
      setIsWorkspaceForcingCollapse(true);
    } else if (layoutMode === 'chat' && isWorkspaceForcingCollapse) {
      // Restore manual state when returning to chat mode
      setIsSidebarCollapsed(manualCollapsedStateRef.current);
      setIsWorkspaceForcingCollapse(false);
      userManuallyExpandedRef.current = false; // Reset manual expansion flag
    }
  }, [layoutMode, isWorkspaceForcingCollapse, isSidebarCollapsed]);

  // Handle user manually expanding sidebar in workspace mode
  const handleSidebarToggle = (collapsed: boolean): void => {
    setIsSidebarCollapsed(collapsed);
    // If user manually expands sidebar in workspace mode, set flag to prevent auto-collapse
    if (!collapsed && layoutMode === 'workspace') {
      userManuallyExpandedRef.current = true;
      setIsWorkspaceForcingCollapse(false);
    } else if (collapsed && layoutMode === 'workspace') {
      // If user manually collapses in workspace mode, allow auto-collapse to work again
      userManuallyExpandedRef.current = false;
    }
  };

  // Unified initialization: handles both URL hydration AND chat listener setup
  // This fixes the race condition where both effects ran independently
  // Skip on admin routes - admin pages don't need project hydration
  useEffect(() => {
    if (isAdminRoute) {
      return;
    }

    const urlProjectId = searchParams.get('projectId');

    const initializeChat = async () => {
      // Acquire project switch lock — ensures only one init flow runs at a time
      const signal = acquireProjectSwitchLock();

      if (urlProjectId && urlProjectId !== currentProjectId) {
        console.log('🔄 [URL HYDRATE] Hydrating project from URL:', urlProjectId);

        // Skip hydration for temporary projects - they have no persisted data
        // Just start a fresh new chat view
        if (urlProjectId.startsWith('temp-')) {
          console.log('🔄 [URL HYDRATE] Temporary project detected - starting fresh chat');
          dispatch(setChatHistoryLoading(false));
          dispatch(setLayoutMode('chat'));
          // Start a new project flow instead of trying to load non-existent data
          dispatch(startNewProject());  // startNewProject acquires its own lock
          return;
        }

        // Show loading spinner immediately for instant feedback
        dispatch(setChatHistoryLoading(true));

        // CRITICAL: Set project ID BEFORE initializing chat listener
        // This ensures bootstrap includes the correct project_id
        console.log('🔄 [URL HYDRATE] Setting project ID before chat listener init:', urlProjectId);
        dispatch(setProjectId(urlProjectId));

        // Initialize chat listener AFTER project ID is set
        // Bootstrap will now include the correct project_id
        console.log('🎧 [INIT] Dispatching initializeChatListener (with project ID already set)');
        await dispatch(initializeChatListener(signal));
        if (signal.aborted) {
          console.log('🔒 [URL HYDRATE] Superseded after initializeChatListener — aborting');
          return;
        }

        // Fetch full project data, rehydrate conversation, then project context
        dispatch(fetchProject(urlProjectId))
          .unwrap()
          .then(() => {
            if (signal.aborted) return;
            console.log('✅ [URL HYDRATE] Project fetched - starting rehydration');
            return dispatch(rehydrateConversation(urlProjectId));
          })
          .then(() => {
            if (signal.aborted) return;
            console.log('✅ [URL HYDRATE] URL hydration complete');
          })
          .catch((error) => {
            if (signal.aborted) return;
            console.error('❌ [URL HYDRATE] Error in URL hydration:', error);
            // On any error (including 404), clear loading and show fresh chat view
            console.log('🔄 [URL HYDRATE] Falling back to fresh chat view');
            dispatch(setChatHistoryLoading(false));
            dispatch(setLayoutMode('chat'));
            dispatch(startNewProject());
          });
      } else {
        // No URL project ID - just initialize chat listener normally (will create new project)
        console.log('🎧 [INIT] Dispatching initializeChatListener (no URL project ID)');
        dispatch(initializeChatListener(signal));
      }
    };

    initializeChat();
  }, [isAdminRoute]); // Run only on mount - isAdminRoute for guard


  // NOTE: Chat listener initialization is now handled in the unified initialization effect above
  // This prevents the race condition between URL hydration and bootstrap

  // Update URL when project ID changes
  // Skip on admin routes - admin pages don't update project URL params
  useEffect(() => {
    if (isAdminRoute) {
      return;
    }

    if (currentProjectId) {
      // Check if URL already has this project ID to avoid unnecessary updates
      const currentUrlProjectId = searchParams.get('projectId');
      if (currentUrlProjectId !== currentProjectId) {
        const params = new URLSearchParams(searchParams);
        params.set('projectId', currentProjectId);
        setSearchParams(params, { replace: true });
      }
    }
  }, [currentProjectId, searchParams, setSearchParams, isAdminRoute]);

  const toggleMobileSidebar = (): void => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = (): void => {
    setIsMobileSidebarOpen(false);
  };

  return (
    <div
      className="h-screen tablet:h-[100dvh] flex overflow-hidden"
      style={{ backgroundColor: "#F9F8F3", ...tabletKeyboardStyle }}
    >
      {/* Desktop Sidebar - persistent on desktop (>=1200px), full height - hidden on admin routes */}
      {!isAdminRoute && (
        <div className="hidden desktop:block overflow-hidden">
          <Sidebar_Component
            isCollapsed={isSidebarCollapsed}
            onCollapseToggle={handleSidebarToggle}
            onStartProject={handleStartProject}
            isWorkspaceForcingCollapse={isWorkspaceForcingCollapse}
            projects={userProjects}
            currentProjectId={currentProjectId}
            onProjectClick={handleProjectClick}
            projectsLoading={userProjectsLoading}
          />
        </div>
      )}

      {/* Mobile + Tablet Sidebar Overlay - hidden on admin routes */}
      {!isAdminRoute && isMobileSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 desktop:hidden"
            onClick={closeMobileSidebar}
          />
          {/* Sidebar overlay */}
          <div className="fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out desktop:hidden shadow-[4px_0_12px_rgba(0,0,0,0.15)]">
            <Sidebar_Component
              isCollapsed={false}
              onClose={closeMobileSidebar}
              onStartProject={handleStartProject}
              projects={userProjects}
              currentProjectId={currentProjectId}
              onProjectClick={(projectId) => {
                closeMobileSidebar();
                handleProjectClick(projectId);
              }}
              projectsLoading={userProjectsLoading}
            />
          </div>
        </>
      )}

      {/* Main content area with header — overflow-hidden prevents iOS WebKit
          from scrolling this container when focusing the chat input (which would
          shift the header off-screen by exactly the header height) */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <Header_Component
          onToggleSidebar={toggleMobileSidebar}
          isMobileSidebarOpen={isMobileSidebarOpen}
          onOpenWelcomeModal={!isAdminRoute ? () => setIsWelcomeModalOpen(true) : undefined}
        />

        {/* Main Content Area - responsive padding with smooth transitions */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full transition-[width] duration-200">
            <MainContentArea_Component />
          </div>
        </div>
      </div>

      {/* Welcome Modal - auto-opens on first visit, re-openable via header button */}
      {!isAdminRoute && (
        <WelcomeModal
          isOpen={isWelcomeModalOpen}
          onClose={() => {
            setIsWelcomeModalOpen(false);
            localStorage.setItem(WELCOME_MODAL_KEY, 'true');
          }}
        />
      )}

    </div>
  );
}
